import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { api, authHeaders, describeError } from "../lib/api";
import { num } from "../lib/format";
import {
  CONSULT_STATUS_LABEL,
  CONSULT_STATUS_ORDER,
  CONSULT_TOPIC_LABEL,
  CONSULT_TOPIC_ORDER,
  INCIDENT_STATUS_LABEL,
  INCIDENT_STATUS_ORDER,
} from "../lib/services";
import { DonutChart, RankBars, type DonutSlice } from "./charts";
import { Panel } from "./console";

/**
 * แผงสรุป ແຈ້ງເຫດ / ນັດປຶກສາ บนจอภาพรวม (ADR-0027) — นับทุกเรื่องในเขตของผู้ใช้ ณ ตอนนี้
 * ไม่ขึ้นกับช่วงวันที่/ตัวกรองบ้านของจอ (API ทั้งสองตัวไม่รับตัวกรองนั้น) จึงต้องบอกไว้ใต้แผง
 *
 * นัดปรึกษาใช้ `/stats` ที่มีแค่จำนวน — การเงิน/กวดกาอ่านรายละเอียดไม่ได้ (ความลับ ADR-0027 ข้อ 2)
 */

const SCOPE_NOTE = "ນັບທຸກເລື່ອງໃນເຂດ ຕອນນີ້ — ບໍ່ຂຶ້ນກັບຊ່ວງວັນທີ ແລະ ຕົວກອງບ້ານຂ້າງເທິງ";

const INCIDENT_COLOR: Record<string, string> = {
  new: "var(--gold-500)",
  acknowledged: "var(--navy-500)",
  in_progress: "var(--navy-700)",
  resolved: "var(--success)",
  rejected: "var(--red-600)",
};
const CONSULT_COLOR: Record<string, string> = {
  requested: "var(--gold-500)",
  scheduled: "var(--navy-600)",
  completed: "var(--success)",
  declined: "var(--red-600)",
  cancelled: "var(--ink-400)",
};

async function fetchIncidentCounts() {
  // limit 1: ต้องการแค่ `counts` ทั้งเขต ไม่ต้องการรายการ
  const { data, error, response } = await api.GET("/v1/admin/incidents", {
    params: { query: { limit: 1 } },
    headers: authHeaders(),
  });
  if (error || !data) throw new Error(describeError(error, response.status));
  return data.counts;
}

async function fetchConsultationStats() {
  const { data, error, response } = await api.GET("/v1/admin/consultations/stats", { headers: authHeaders() });
  if (error || !data) throw new Error(describeError(error, response.status));
  return data;
}

export function IncidentsSummaryPanel() {
  const q = useQuery({ queryKey: ["incidents", "counts"], queryFn: fetchIncidentCounts });
  const slices: DonutSlice[] = INCIDENT_STATUS_ORDER.map((s) => ({
    label: INCIDENT_STATUS_LABEL[s],
    value: q.data?.[s] ?? 0,
    color: INCIDENT_COLOR[s] ?? "var(--ink-400)",
  })).filter((s) => s.value > 0);
  return (
    <Panel title="ແຈ້ງເຫດ" note={SCOPE_NOTE}>
      {q.isError ? (
        <p className="field-error">ໂຫຼດບໍ່ສຳເລັດ: {q.error.message}</p>
      ) : (
        <DonutChart
          slices={slices}
          title="ເລື່ອງແຈ້ງແຍກຕາມສະຖານະ"
          totalLabel="ເລື່ອງທັງໝົດ"
          emptyLabel={q.isPending ? "ກຳລັງໂຫຼດ…" : "ຍັງບໍ່ມີເລື່ອງແຈ້ງໃນເຂດນີ້"}
        />
      )}
      <Link to="/incidents" className="link-action">
        ເບິ່ງລາຍການແຈ້ງເຫດ
      </Link>
    </Panel>
  );
}

/** สถิตินัดปรึกษา — จำนวนต่อสถานะ + ต่อหัวข้อ ไม่มีเนื้อหา */
export function ConsultationsStatsPanel({ showLink = false }: { showLink?: boolean }) {
  const q = useQuery({ queryKey: ["consultations", "stats"], queryFn: fetchConsultationStats });
  const slices: DonutSlice[] = CONSULT_STATUS_ORDER.map((s) => ({
    label: CONSULT_STATUS_LABEL[s],
    value: q.data?.by_status[s] ?? 0,
    color: CONSULT_COLOR[s] ?? "var(--ink-400)",
  })).filter((s) => s.value > 0);
  const empty = q.isPending ? "ກຳລັງໂຫຼດ…" : "ຍັງບໍ່ມີການນັດປຶກສາໃນເຂດນີ້";
  return (
    <Panel
      title="ນັດປຶກສາ"
      note={`${SCOPE_NOTE} · ເນື້ອໃນເປັນຄວາມລັບ ສະແດງແຕ່ຈຳນວນ`}
    >
      {q.isError ? (
        <p className="field-error">ໂຫຼດບໍ່ສຳເລັດ: {q.error.message}</p>
      ) : (
        <div className="grid2 grid2--even" style={{ marginBottom: 0 }}>
          <DonutChart slices={slices} title="ນັດປຶກສາແຍກຕາມສະຖານະ" totalLabel="ນັດທັງໝົດ" emptyLabel={empty} />
          <RankBars
            items={CONSULT_TOPIC_ORDER.map((t) => ({ key: t, label: CONSULT_TOPIC_LABEL[t], value: q.data?.by_topic[t] ?? 0 }))
              .filter((i) => i.value > 0)
              .sort((a, b) => b.value - a.value)}
            format={(v) => `${num(v)} ເລື່ອງ`}
            emptyLabel={empty}
          />
        </div>
      )}
      {showLink ? (
        <Link to="/consultations" className="link-action">
          ເປີດຄິວນັດປຶກສາ
        </Link>
      ) : null}
    </Panel>
  );
}
